import { useEffect } from 'react';
import {
  Font,
  FONT_PAIRS,
  FONT_URLS,
  FontPair,
} from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/font-config';

/**
 * Dynamically loads a Google Font by injecting a link element.
 * Browser handles caching and only downloads when font is actually used.
 */
export const loadFont = (font: Font) => {
  const fontLinkId = `font-link-${font.replace(/\s+/g, '-').toLowerCase()}`;

  // Check if already loaded
  if (document.getElementById(fontLinkId)) {
    return;
  }

  const link = document.createElement('link');
  link.id = fontLinkId;
  link.href = FONT_URLS[font];
  link.rel = 'stylesheet';
  document.head.appendChild(link);
};

type UseThemeFontsOptions = {
  fontPair?: FontPair | null;
  preloadAll?: boolean;
};

/**
 * Loads fonts for a specific theme or preloads all theme fonts.
 * @param options.fontPair - Specific font pair to load
 * @param options.preloadAll - If true, loads all theme fonts for quick switching
 */
export const useThemeFonts = ({ fontPair, preloadAll = false }: UseThemeFontsOptions) => {
  useEffect(() => {
    const fontsToLoad = new Set<Font>();

    if (preloadAll) {
      // Load all fonts from all themes
      Object.values(FONT_PAIRS).forEach((pair) => {
        fontsToLoad.add(pair.header);
        fontsToLoad.add(pair.body);
      });
    } else {
      // Load only fonts for current theme
      if (!fontPair) {
        return;
      }
      const pair = FONT_PAIRS[fontPair];
      fontsToLoad.add(pair.header);
      fontsToLoad.add(pair.body);
    }

    fontsToLoad.forEach(loadFont);
  }, [fontPair, preloadAll]);
};

export default useThemeFonts;
